import clsx from 'clsx'
import Clickable from 'components/Common/Clickable'
import { mediaHoverable } from 'lib/theme/mediaQueries'
import { rem } from 'polished'
import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'
import { AccommodationMonthView } from './AccommodationCalendarMonthPicker'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import Heading from 'LuxKit/Typography/Heading'
import { INTL_SHORT_MONTH } from 'constants/dateFormats'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Caption from 'LuxKit/Typography/Caption'
import Plural from 'components/utils/Formatters/Plural'
import BodyText from 'LuxKit/Typography/BodyText'
import FormatCurrency from 'components/Common/FormatCurrency'
import Group from 'components/utils/Group'
import TextLink from 'LuxKit/TextLink'
import SolidFireIcon from 'LuxKit/Icons/solid/SolidFireIcon'

const MonthCell = styled(Clickable)`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border: 1px solid ${props => props.theme.palette.neutral.default.six};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  box-shadow: none;
  transition: background-color 0.2s, box-shadow 0.2s;
  border-radius: ${props => props.theme.borderRadius.S};
  padding: ${rem(12)};
  min-height: ${rem(70)};

  &:not(:disabled) {
    ${mediaHoverable} {
      &:hover {
        box-shadow: ${props => props.theme.shadow.bottom.medium};
      }
    }
    &:focus {
      box-shadow: ${props => props.theme.shadow.bottom.medium};
    }
  }

  &.sold-out {
    background-color: ${props => props.theme.palette.neutral.default.seven};
  }

  &.selected {
    background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
    color: ${props => props.theme.palette.neutral.default.one};
  }
`

const CellTitle = styled(Group)`
  min-height: ${rem(24)};
`

interface Props {
  view: AccommodationMonthView
  onSelect?: (view: AccommodationMonthView) => void
  cheapest?: boolean
  priceType?: 'default' | 'member'
}

function AccommodationCalendarMonthCell(props: Props) {
  const { view, onSelect, cheapest, priceType } = props
  const dateFormatter = useIntlDateFormatter()
  const date = useMemo(() => new Date(view.year, view.month, 1), [view.month, view.year])

  const onMonthCellClick = useCallback(() => onSelect?.(view), [onSelect, view])

  let priceColour: React.ComponentProps<typeof BodyText>['colour']
  if (cheapest) {
    priceColour = 'highlight-secondary'
  } else if (priceType === 'member') {
    priceColour = 'lux-plus'
  }

  return <MonthCell
    disabled={view.soldOut && !view.soldOutExtraActionText}
    className={clsx({ 'sold-out': view.soldOut })}
    onClick={onMonthCellClick}
  >
    <CellTitle direction="horizontal" verticalAlign="center" horizontalAlign="space-between">
      <Group direction="horizontal" gap={4}>
        <Heading variant="heading6">
          {dateFormatter(date, INTL_SHORT_MONTH)}
        </Heading>
        {view.featured && <SolidFireIcon colour="urgency" size="XS" />}
      </Group>
      {view.label}
    </CellTitle>
    <VerticalSpacer gap={2}>
      {!view.pricing && view.soldOut && <VerticalSpacer gap={2}>
        <Caption variant="large" colour="neutral-four">SOLD OUT</Caption>
        {!!view.soldOutExtraActionText && <TextLink size="caption" weight="regular">
          {view.soldOutExtraActionText}
        </TextLink>}
      </VerticalSpacer>}
      {!!view.pricing && <>
        <Caption variant="large" colour="neutral-three">
          {view.duration && <Plural count={view.duration} singular={view.durationUnit} withCount />} from
        </Caption>
        <div>
          <BodyText variant="medium" weight="bold" as="span" colour={priceColour}>
            <FormatCurrency value={view.pricing.price} format="roundedDollar" />
          </BodyText>{' '}
          <Caption variant="large" colour="neutral-three" as="span">
            {view.pricing.saleUnit}
          </Caption>
        </div>
      </>}
    </VerticalSpacer>
  </MonthCell>
}

export default AccommodationCalendarMonthCell
